import { useEffect, useRef, useState } from 'react';
import Icon from '../../../../Icon/Icon';
import { useTranslation } from 'react-i18next';
import { isHtmlPreviewUrl } from '../../HtmlCreativePreview/HtmlCreativePreview';
import CreativePreviewModal from '../../HtmlCreativePreview/CreativePreviewModal';

function FileCard({
    file,
    displayName,
    onRemove,
    properties,
    processing,
    waitingForProcessing,
    uploadStatus,
    error,
    onRetry,
    previewUrl,
    previewSize,
    assignedCount = 0,
    compatibleGroups = [],
    onReassignToGroup,
    onReassignAuto,
}) {
    const { t } = useTranslation();
    const [deleting, setDeleting] = useState(false);
    const [fileType, setFileType] = useState(properties.type);
    const [preview, setPreview] = useState(null);
    const [isReassignMenuOpen, setIsReassignMenuOpen] = useState(false);
    const [pendingActionHash, setPendingActionHash] = useState(null);
    const reassignMenuRef = useRef(null);
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    // Dynamic creatives are only loaded on demand and only from their remote
    // page (local zips and the default thumbnail cannot be rendered).
    const previewModal =
        properties.type === 'dynamic'
            ? isHtmlPreviewUrl(previewUrl) && {
                  type: 'html',
                  url: previewUrl,
                  size: previewSize,
              }
            : ['image', 'video'].includes(properties.type) &&
              preview && {
                  type: properties.type,
                  url: preview,
                  size: { width: properties.width, height: properties.height },
              };
    const canOpenPreview =
        Boolean(previewModal) &&
        !processing &&
        !waitingForProcessing &&
        uploadStatus !== 'error';

    useEffect(() => {
        setFileType(properties.type);
    }, [properties.type]);

    useEffect(() => {
        if (!isReassignMenuOpen) return undefined;

        const handleClickOutside = (event) => {
            if (
                reassignMenuRef.current &&
                !reassignMenuRef.current.contains(event.target)
            ) {
                setIsReassignMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isReassignMenuOpen]);

    // Fallback: solo si NO tenemos previewUrl (para no refactorizar todo)
    useEffect(() => {
        if (previewUrl) {
            setPreview(previewUrl);
            return;
        }

        if (!file) return;

        let reader = new FileReader();
        reader.onloadend = () => setPreview(reader.result);
        reader.readAsDataURL(file);

        return () => {
            reader.onloadend = null;
        };
    }, [file, previewUrl]);

    function useThumbnail(type) {
        switch (type) {
            case 'image':
            case 'video':
                return true;
        }
        return false;
    }

    function getIcon(type) {
        switch (type) {
            case 'vast':
                return 'link';
        }
        return 'code';
    }

    // Check if file is too large taking width, height and duration into account
    // @returns int - 0 if in normal limits, 1 if its 30% over, 2 otherwise
    function analyzeFileSize(type, size, width, height, duration) {
        switch (type) {
            case 'image':
                let frameSize = width * height * 4; // 4 bytes per pixel

                // Take into account average compression ratio for images
                // assuming PNG/JPEG compression
                // average compression ratio is around 10:1, but we use 5:1 to
                // be more conservative
                // SEE: https://reduceimages.online/blog/jpeg-png-webp-compression-comparison
                frameSize = frameSize * 0.2;

                if (size > frameSize * 1.3) {
                    return 2;
                } else if (size > frameSize) {
                    return 1;
                }
                break;
            case 'video':
                let videoFrameSize = width * height * 4; // 4 bytes per pixel

                // Take into account average compression ratio for videos
                videoFrameSize = videoFrameSize * 0.2;

                let estimatedSize = videoFrameSize * (duration * 30); // assuming 30 fps
                if (size > estimatedSize * 1.3) {
                    return 2;
                } else if (size > estimatedSize) {
                    return 1;
                }
                break;
        }
        return 0;
    }

    return (
        <div
            key={properties.hash}
            className="relative flex flex-col border border-gray-300 rounded-lg p-4 hover:shadow-xl hover:border-blue-500 transition-shadow transition-all duration-200 cursor-pointer"
            dataTestid="FileCard">
            <div
                className="overflow-hidden w-full flex justify-center items-center mb-2 rounded-lg border border-gray-300 text-gray-500 relative"
                style={{
                    aspectRatio: `${properties.width} / ${properties.height}`,
                }}>
                {useThumbnail(properties.type) &&
                    properties.type == 'image' && (
                        <div
                            dataTestid="FileCard-ImagePreview"
                            className="w-full h-full flex justify-center items-center">
                            <img
                                src={preview}
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    )}
                {useThumbnail(properties.type) &&
                    properties.type == 'video' && (
                        <div
                            dataTestid="FileCard-VideoPreview"
                            className="w-full h-full flex justify-center items-center">
                            <video
                                key={preview}
                                src={preview}
                                controls
                                className="max-w-full max-h-full object-contain"
                            />
                        </div>
                    )}
                {!useThumbnail(properties.type) &&
                    properties.type == 'dynamic' && (
                        <div
                            className="w-full h-full bg-gradient-to-br from-black via-gray-800 to-black flex items-center justify-center flex-col"
                            dataTestid="FileCard-DynamicPreview">
                            <Icon
                                name="code"
                                size="2x"
                                className="text-white opacity-90"
                            />
                            <span className="text-white opacity-90 mt-2">
                                {t(
                                    'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.dynamic-creative'
                                )}
                            </span>
                        </div>
                    )}
                {isPreviewModalOpen && canOpenPreview && (
                    <CreativePreviewModal
                        type={previewModal.type}
                        url={previewModal.url}
                        title={file?.name || displayName || properties.name}
                        size={previewModal.size}
                        onClose={() => setIsPreviewModalOpen(false)}
                    />
                )}
                {(processing || waitingForProcessing) && (
                    <div
                        role="status"
                        aria-live="polite"
                        data-testid={
                            processing ? 'FileCard-processing' : 'FileCard-waiting'
                        }
                        className={`absolute inset-0 z-10 flex flex-col items-center justify-center text-white ${
                            processing
                                ? 'bg-gray-900 bg-opacity-70'
                                : 'bg-blue-900 bg-opacity-60'
                        }`}>
                        <Icon
                            name={processing ? 'circle-notch' : 'clock'}
                            size="2x"
                            className={processing ? 'animate-spin mb-2' : 'mb-2'}
                        />
                        <span className="text-xs font-medium uppercase">
                            {t(
                                `sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.${
                                    processing
                                        ? 'processing-video'
                                        : 'waiting-for-processing'
                                }`
                            )}
                        </span>
                    </div>
                )}
                {uploadStatus === 'error' && (
                    <div
                        role="alert"
                        data-testid="FileCard-error"
                        className="absolute inset-0 z-10 flex flex-col items-center justify-center p-2 text-center text-white bg-red-900 bg-opacity-80 overflow-hidden">
                        <Icon
                            name="exclamation-triangle"
                            size="lg"
                            className="mb-1"
                        />
                        <span className="text-xs font-medium leading-tight">
                            {error ||
                                t(
                                    'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.upload-failed'
                                )}
                        </span>
                    </div>
                )}
            </div>
            {uploadStatus === 'error' && onRetry && (
                <button
                    type="button"
                    className="w-full mb-2 px-3 py-1 rounded-md bg-red-700 text-white text-xs font-medium uppercase hover:bg-red-800"
                    onClick={() => onRetry(properties.hash)}
                    data-testid="FileCard-retry-button">
                    {t(
                        'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.retry'
                    )}
                </button>
            )}
            <div
                className="font-light text-sm mb-1 overflow-hidden truncate whitespace-nowrap"
                dataTestid="FileCard-fileName">
                {file?.name || displayName}
            </div>

            {/*  //TODO: not file from backend, make it work only in local case
            <div className="font-normal text-xs text-gray-500 flex flex-row justify-between">
                {analyzeFileSize(
                    properties.type,
                    file.size || 1,
                    properties.width,
                    properties.height,
                    properties.duration
                ) === 1 ? (
                    <span>
                        {(file.size || 1 / (1024 * 1024)).toFixed(2)} MB{' '}
                        <span
                            className="animate-pulse ml-1 text-red-500"
                            dataTestid="FileCard-FileTooLargeWarning">
                            <Icon
                                name="exclamation-circle"
                                className="mr-1"
                            />
                            File too large
                        </span>
                    </span>
                ) : (autoAssignCreativesFromFilesMap(map);

                    <span dataTestid="FileCard-file-size">
                        {(file?.size || 1 / (1024 * 1024)).toFixed(2)} MB
                    </span>
                )}
            </div> */}
            {/* Pinned to the bottom so it lines up across the cards of a row,
                whatever the thumbnail aspect ratio. */}
            <div
                className="mt-auto flex flex-col items-start gap-2 pt-1"
                data-testid="FileCard-details">
                <div className="flex flex-col items-start gap-2">
                    <span
                        className="text-center bg-blue-200 text-gray-600 text-xs w-fit px-2 py-1 rounded-md inline uppercase"
                        dataTestid="FileCard-file-type">
                        {fileType}
                    </span>
                    <span
                        className="text-center bg-blue-200 text-gray-600 text-xs w-fit whitespace-nowrap px-2 py-1 rounded-md inline uppercase"
                        dataTestid="FileCard-file-dimensions">
                        {properties.width} x {properties.height}
                    </span>
                    {useThumbnail(properties.type) &&
                        properties.type == 'video' && (
                            <span
                                className="text-center bg-blue-200 text-gray-600 text-xs w-fit px-2 py-1 rounded-md inline uppercase"
                                dataTestid="FileCard-file-duration">
                                {Number.isFinite(properties.duration) &&
                                properties.duration > 0
                                    ? Math.round(properties.duration)
                                    : '—'}{' '}
                                {t(
                                    'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.seconds-short'
                                )}
                            </span>
                        )}
                </div>
                {assignedCount === 0 ? (
                    <span
                        className="bg-amber-100 text-amber-800 text-xs w-fit px-2 py-1 rounded-md inline uppercase font-medium"
                        dataTestid="FileCard-unassigned">
                        <Icon
                            name="exclamation-circle"
                            size="sm"
                            className="mr-1"
                        />
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.unassigned'
                        )}
                    </span>
                ) : (
                    <span
                        className="bg-green-100 text-green-800 text-xs w-fit px-2 py-1 rounded-md inline uppercase font-medium"
                        dataTestid="FileCard-assigned-count">
                        <Icon
                            name="check-circle"
                            size="sm"
                            className="mr-1"
                        />
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.assigned',
                            { count: assignedCount }
                        )}
                    </span>
                )}
                {canOpenPreview && (
                    <button
                        type="button"
                        className="flex w-full items-center justify-center whitespace-nowrap px-3 py-1.5 rounded-md bg-gray-700 bg-opacity-80 hover:bg-opacity-100 shadow text-white text-xs font-medium focus:outline-none"
                        onClick={() => setIsPreviewModalOpen(true)}
                        data-testid="FileCard-open-preview">
                        <Icon name="eye" size="sm" className="mr-1" />
                        {t(
                            'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.view-preview'
                        )}
                    </button>
                )}
            </div>
            <button
                className="absolute -top-2 -right-2 text-gray-400 block bg-gray-300 rounded-full p-1 px-2 hover:text-red-600"
                onClick={async () => {
                    setDeleting(true);
                    await onRemove(properties.hash);
                    setDeleting(false);
                }}
                dataTestid="FileCard-remove-button">
                <Icon name="times" />
            </button>
            {/* Reassign kebab menu */}
            <div
                ref={reassignMenuRef}
                className="absolute top-2 left-2 z-20"
                dataTestid="FileCard-reassign-wrapper">
                <button
                    type="button"
                    className="bg-gray-300 rounded-full p-1 px-3 text-gray-600 hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!onReassignToGroup || !onReassignAuto}
                    onClick={() =>
                        setIsReassignMenuOpen((current) => !current)
                    }
                    dataTestid="FileCard-reassign-toggle">
                    <Icon name="ellipsis-v" />
                </button>

                {isReassignMenuOpen && (
                    <div
                        className="absolute top-7 left-0 w-56 rounded-lg border border-gray-200 bg-white shadow-xl overflow-hidden z-30"
                        dataTestid="FileCard-reassign-dropdown">
                        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-100">
                            {t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.reassign'
                            )}
                        </div>
                        <div className="max-h-56 overflow-y-auto">
                            {compatibleGroups?.length > 0 ? (
                                compatibleGroups.map((group) => (
                                    <button
                                        key={group.hash}
                                        type="button"
                                        className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 transition border-b border-gray-100 last:border-b-0 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-between"
                                        disabled={
                                            pendingActionHash === group.hash
                                        }
                                        onClick={async () => {
                                            setPendingActionHash(group.hash);
                                            try {
                                                await onReassignToGroup?.(
                                                    properties.hash,
                                                    group.hash
                                                );
                                            } finally {
                                                setPendingActionHash(null);
                                            }
                                        }}
                                        dataTestid="FileCard-reassign-to-group">
                                        <span className="truncate text-gray-800">
                                            {group.label}
                                        </span>
                                        <span className="flex items-center ml-2">
                                            {group.isCurrentlyAssigned && (
                                                <Icon
                                                    name="check"
                                                    size="sm"
                                                    className="text-green-500"
                                                />
                                            )}
                                        </span>
                                    </button>
                                ))
                            ) : (
                                <div className="px-3 py-3 text-sm text-gray-500">
                                    {t(
                                        'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.reassign-no-groups'
                                    )}
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className="w-full px-3 py-2 text-left text-sm font-medium text-blue-700 hover:bg-blue-50 transition border-t border-gray-200 disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center"
                            disabled={!onReassignAuto}
                            onClick={async () => {
                                setPendingActionHash('__auto__');
                                try {
                                    await onReassignAuto?.(properties.hash);
                                    setIsReassignMenuOpen(false);
                                } finally {
                                    setPendingActionHash(null);
                                }
                            }}
                            dataTestid="FileCard-reassign-auto">
                            <Icon
                                name="sync"
                                size="sm"
                                className="mr-2"
                            />
                            {t(
                                'sections.campaigns.new-campaign.wizard-creatives.creatives.file-card.reassign-to-all'
                            )}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FileCard;
